// @ts-nocheck
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Eye, Edit } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface Orcamento {
  id: string;
  nome: string;
  status: string;
  data_validade: string;
  created_at: string;
  projetos?: { nome: string };
  clientes?: { nome: string };
  empresas?: { razao_social: string };
}

interface FilterOptions {
  projetos: Array<{ id: string; nome: string }>;
  clientes: Array<{ id: string; nome: string }>;
  empresas: Array<{ id: string; razao_social: string }>;
  fornecedores: Array<{ id: string; razao_social: string }>;
  locais: Array<{ id: string; nome: string }>;
}

export default function PropostasOrcamentos() {
  const [orcamentos, setOrcamentos] = useState<Orcamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    projeto: "",
    cliente: "",
    empresa: "",
    fornecedor: "",
    local: "",
    status: "",
  });
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    projetos: [],
    clientes: [],
    empresas: [],
    fornecedores: [],
    locais: [],
  });

  const navigate = useNavigate();
  const { toast } = useToast();

  const loadFilterOptions = async () => {
    try {
      const [projetos, clientes, empresas, fornecedores, locais] = await Promise.all([
        supabase.from("projetos").select("id, nome"),
        supabase.from("clientes").select("id, nome").eq("ativo", true),
        supabase.from("empresas").select("id, razao_social").eq("ativo", true),
        supabase.from("fornecedores").select("id, razao_social").eq("ativo", true),
        supabase.from("locais").select("id, nome").eq("ativo", true),
      ]);

      setFilterOptions({
        projetos: projetos.data || [],
        clientes: clientes.data || [],
        empresas: empresas.data || [],
        fornecedores: fornecedores.data || [],
        locais: locais.data || [],
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar opções de filtro",
        variant: "destructive",
      });
    }
  };

  const loadOrcamentos = async () => {
    try {
      let query = supabase
        .from("orcamentos")
        .select(`
          id,
          nome,
          status,
          data_validade,
          created_at,
          projetos(nome),
          clientes(nome),
          empresas(razao_social)
        `)
        .order("created_at", { ascending: false });

      if (filters.projeto) query = query.eq("id_projeto", filters.projeto);
      if (filters.cliente) query = query.eq("id_cliente", filters.cliente);
      if (filters.empresa) query = query.eq("id_empresa", filters.empresa);
      if (filters.fornecedor) query = query.eq("id_fornecedor", filters.fornecedor);
      if (filters.local) query = query.eq("id_local", filters.local);
      if (filters.status) query = query.eq("status", filters.status);

      const { data, error } = await query;

      if (error) throw error;
      setOrcamentos(data || []);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar orçamentos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadOrcamentos();
  }, [filters]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value === "all" ? "" : value }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Orçamentos</h1>
          <p className="text-muted-foreground">
            Gerencie os orçamentos das propostas
          </p>
        </div>
        <Button onClick={() => navigate("/intranet/propostas/orcamentos/new")}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Orçamento
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <label className="text-sm font-medium">Projeto</label>
              <Select value={filters.projeto || "all"} onValueChange={(value) => handleFilterChange("projeto", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {filterOptions.projetos.map((projeto) => (
                    <SelectItem key={projeto.id} value={projeto.id}>
                      {projeto.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Cliente</label>
              <Select value={filters.cliente || "all"} onValueChange={(value) => handleFilterChange("cliente", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {filterOptions.clientes.map((cliente) => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Empresa</label>
              <Select value={filters.empresa || "all"} onValueChange={(value) => handleFilterChange("empresa", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {filterOptions.empresas.map((empresa) => (
                    <SelectItem key={empresa.id} value={empresa.id}>
                      {empresa.razao_social}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Fornecedor</label>
              <Select value={filters.fornecedor || "all"} onValueChange={(value) => handleFilterChange("fornecedor", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {filterOptions.fornecedores.map((fornecedor) => (
                    <SelectItem key={fornecedor.id} value={fornecedor.id}>
                      {fornecedor.razao_social}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Local</label>
              <Select value={filters.local || "all"} onValueChange={(value) => handleFilterChange("local", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {filterOptions.locais.map((local) => (
                    <SelectItem key={local.id} value={local.id}>
                      {local.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={filters.status || "all"} onValueChange={(value) => handleFilterChange("status", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="rascunho">Rascunho</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="aprovado">Aprovado</SelectItem>
                  <SelectItem value="rejeitado">Rejeitado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Orçamentos */}
      <Card>
        <CardHeader>
          <CardTitle>Orçamentos Cadastrados</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Projeto</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Prazo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Data de Criação</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orcamentos.map((orcamento) => (
                  <TableRow key={orcamento.id}>
                    <TableCell className="font-medium">{orcamento.nome}</TableCell>
                    <TableCell>{orcamento.projetos?.nome || "-"}</TableCell>
                    <TableCell>{orcamento.clientes?.nome || "-"}</TableCell>
                    <TableCell>{orcamento.empresas?.razao_social || "-"}</TableCell>
                    <TableCell>
                      {orcamento.data_validade ? formatDate(orcamento.data_validade) : "-"}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        orcamento.status === "aprovado" ? "bg-green-100 text-green-800" :
                        orcamento.status === "pendente" ? "bg-yellow-100 text-yellow-800" :
                        orcamento.status === "rejeitado" ? "bg-red-100 text-red-800" :
                        "bg-gray-100 text-gray-800"
                      }`}>
                        {orcamento.status || "Rascunho"}
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(orcamento.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/intranet/propostas/orcamentos/${orcamento.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/intranet/propostas/orcamentos/${orcamento.id}/edit`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {orcamentos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Nenhum orçamento encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}